// Mapas gerados pelo servidor: binário compactado com a pilha de objetos de cada tile,
// mais os metadados (onde se pode pisar, escadas, portais, rótulos).

export interface MapObj {
  s: number[]; pw?: number; ph?: number; pd?: number; l?: number;
  a?: [number, number];            // animação: quantidade de fases, duração de cada fase (ms)
  g?: number; c?: 1; b?: 1; t?: 1; u?: 1;
  sh?: [number, number]; el?: number; mm?: number;
}

export interface MapMeta {
  name: string; width: number; height: number; floors: number; zbase: number;
  stairs: Array<{ from: [number, number, number]; to: [number, number, number]; kind: 'up' | 'down' }>;
  zones: Array<{ type: string; rect: [number, number, number, number]; floor?: number }>;
  points: Record<string, any>;
  labels: Array<{ text: string; x: number; y: number; kind: string; rect?: [number, number, number, number] }>;
  hunt?: { id: string; name: string; theme: string; level: number; monsters: string[] };
  walk: string[];
}

let objsP: Promise<Record<string, MapObj>> | null = null;
let objsNow: Record<string, MapObj> = {};
export function loadMapObjects(): Promise<Record<string, MapObj>> {
  objsP ??= fetch('/data/mapobjects.json', { cache: 'no-cache' }).then((r) => r.json()).then((o) => (objsNow = o));
  return objsP;
}
export const mapObj = (id: number): MapObj | undefined => objsNow[id];

export class ClientMap {
  readonly w: number; readonly h: number; readonly floors: number;
  private offsets: Uint32Array[] = [];
  private ids: Uint16Array;
  private walk: Uint8Array[];

  constructor(readonly meta: MapMeta, buf: ArrayBuffer) {
    const dv = new DataView(buf);
    const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
    if (magic !== 'NXM1') throw new Error('mapa em formato desconhecido');
    this.w = dv.getUint16(4, true); this.h = dv.getUint16(6, true); this.floors = dv.getUint8(8);
    let p = 10;
    const all: number[] = [];
    for (let f = 0; f < this.floors; f++) {
      const off = new Uint32Array(this.w * this.h + 1);
      for (let i = 0; i < this.w * this.h; i++) {
        off[i] = all.length;
        const n = dv.getUint8(p++);
        for (let k = 0; k < n; k++) { all.push(dv.getUint16(p, true)); p += 2; }
      }
      off[this.w * this.h] = all.length;
      this.offsets.push(off);
    }
    this.ids = Uint16Array.from(all);
    this.walk = meta.walk.map((hex) => {
      const out = new Uint8Array(hex.length / 2);
      for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
      return out;
    });
    // NPCs bloqueiam o tile (igual ao servidor)
    const npcs = (meta.points['npcs'] ?? []) as Array<{ pos: number[] }>;
    for (const n of npcs) { const k = n.pos[1]! * meta.width + n.pos[0]!; const w = this.walk[n.pos[2] ?? 0]; if (w) w[k >> 3]! &= ~(1 << (k & 7)); }
  }

  get name() { return this.meta.name; }
  inside(x: number, y: number, f: number) { return x >= 0 && y >= 0 && x < this.w && y < this.h && f >= 0 && f < this.floors; }

  stack(x: number, y: number, f: number): Uint16Array | null {
    if (!this.inside(x, y, f)) return null;
    const off = this.offsets[f]!, i = y * this.w + x;
    return this.ids.subarray(off[i]!, off[i + 1]!);
  }

  walkable(x: number, y: number, f: number): boolean {
    if (!this.inside(x, y, f)) return false;
    const i = y * this.w + x;
    return ((this.walk[f]![i >> 3]! >> (i & 7)) & 1) === 1;
  }

  groundSpeed(x: number, y: number, f: number): number {
    const st = this.stack(x, y, f);
    const g = st && st.length ? mapObj(st[0]!)?.g : undefined;
    return g ?? 150;
  }

  /** Cor do minimapa do tile (o objeto mais alto que tem cor), no padrão de 216 cores do Tibia. */
  minimapColor(x: number, y: number, f: number): number {
    const st = this.stack(x, y, f);
    if (!st) return 0;
    for (let i = st.length - 1; i >= 0; i--) { const c = mapObj(st[i]!)?.mm; if (c !== undefined) return c; }
    return 0;
  }

  isPortal(x: number, y: number, f: number) {
    const list = this.meta.points['portals'];
    return Array.isArray(list) && list.some((p: number[]) => p[0] === x && p[1] === y && (p[2] ?? 0) === f);
  }
}

const cache = new Map<string, Promise<ClientMap>>();
export function loadMap(name: string): Promise<ClientMap> {
  let p = cache.get(name);
  if (!p) {
    p = (async () => {
      const [metaR, binR] = await Promise.all([fetch(`/maps/${name}.json`, { cache: 'no-cache' }), fetch(`/maps/${name}.bin.gz`, { cache: 'no-cache' }), loadMapObjects()]);
      if (!metaR.ok || !binR.ok) throw new Error('mapa não encontrado: ' + name);
      const meta = await metaR.json() as MapMeta;
      const raw = await binR.arrayBuffer();
      const bytes = new Uint8Array(raw);
      // O servidor manda o arquivo compactado; se algum proxy já descompactou, usamos direto.
      const buf = bytes[0] === 0x1f && bytes[1] === 0x8b
        ? await new Response(new Blob([raw]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer()
        : raw;
      return new ClientMap(meta, buf);
    })();
    cache.set(name, p);
    p.catch(() => cache.delete(name));
  }
  return p;
}

/** Cor de minimapa do Tibia (0..215) em RGB. */
export function minimapRgb(c: number): [number, number, number] {
  return [Math.floor(c / 36) % 6 * 51, Math.floor(c / 6) % 6 * 51, (c % 6) * 51];
}

/** Caminho (A* com diagonais) até o destino, no máximo `limit` tiles. Devolve a lista de direções. */
export function findPath(map: ClientMap, f: number, sx: number, sy: number, tx: number, ty: number, limit = 80): number[] | null {
  if (sx === tx && sy === ty) return [];
  if (!map.walkable(tx, ty, f)) return null;
  const DIRS: Array<[number, number, number]> = [[0, -1, 0], [1, 0, 1], [0, 1, 2], [-1, 0, 3], [1, -1, 4], [1, 1, 5], [-1, 1, 6], [-1, -1, 7]];
  const key = (x: number, y: number) => y * map.w + x;
  const open: Array<[number, number, number, number]> = [[0, sx, sy, 0]]; // [f, x, y, g]
  const came = new Map<number, [number, number]>(); // nó -> [anterior, direção]
  const gScore = new Map<number, number>([[key(sx, sy), 0]]);
  const h = (x: number, y: number) => Math.max(Math.abs(tx - x), Math.abs(ty - y)) * 10;
  let iter = 0;
  while (open.length && iter++ < 6000) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (open[i]![0] < open[bi]![0]) bi = i;
    const [, x, y, g] = open.splice(bi, 1)[0]!;
    if (x === tx && y === ty) {
      const out: number[] = [];
      let k = key(x, y);
      while (came.has(k)) { const [prev, d] = came.get(k)!; out.push(d); k = prev; }
      return out.reverse();
    }
    if (Math.abs(x - sx) > limit || Math.abs(y - sy) > limit) continue;
    for (const [dx, dy, d] of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (!map.walkable(nx, ny, f)) continue;
      if (d > 3 && (!map.walkable(x + dx, y, f) || !map.walkable(x, y + dy, f))) continue; // não corta quina
      const ng = g + (d > 3 ? 25 : 10);
      const k = key(nx, ny);
      if (ng < (gScore.get(k) ?? Infinity)) {
        gScore.set(k, ng); came.set(k, [key(x, y), d]);
        open.push([ng + h(nx, ny), nx, ny, ng]);
      }
    }
  }
  return null;
}
